import React, { useState, lazy, Suspense } from 'react';
import {
  Container,
  Box,
  Typography,
  Tabs,
  Tab,
  useTheme,
  useMediaQuery,
  CircularProgress
} from '@mui/material';
import {
  Business as BusinessIcon,
  Inventory as ProductsIcon,
  Extension as AddonsIcon,
  EventNote as BookingIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import GarageManagement from '../components/system-management/GarageManagement';

// Lazy load components to prevent unnecessary API calls
const ProductManagement = lazy(() => import('../components/system-management/ProductManagement'));
const AddonManagement = lazy(() => import('../components/system-management/AddonManagement'));
const BookingManagement = lazy(() => import('../components/system-management/BookingManagement'));

const SystemManagement = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [activeTab, setActiveTab] = useState(0);
  const [loadedTabs, setLoadedTabs] = useState(new Set([0])); // Track which tabs have been loaded
  const { user } = useAuth();

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setLoadedTabs(prev => new Set([...prev, newValue])); // Mark tab as loaded
  };

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2, md: 4 }, px: { xs: 1, md: 3 } }}>
      <Typography variant={isMobile ? "h5" : "h4"} component="h1" gutterBottom sx={{ mb: { xs: 2, md: 4 } }}>
        System Management
      </Typography>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange} 
          variant={isMobile ? "scrollable" : "standard"}
          scrollButtons="auto"
          sx={{ minHeight: 72 }}
        >
          <Tab 
            icon={<BusinessIcon />} 
            label="Garages" 
            sx={{ minHeight: 72, py: 2 }}
          />
          <Tab 
            icon={<ProductsIcon />} 
            label="Products" 
            sx={{ minHeight: 72, py: 2 }}
          />
          <Tab 
            icon={<AddonsIcon />} 
            label="Addons" 
            sx={{ minHeight: 72, py: 2 }}
          />
          <Tab 
            icon={<BookingIcon />} 
            label="Bookings" 
            sx={{ minHeight: 72, py: 2 }}
          />
        </Tabs>
      </Box>

      {/* Render active tab content */}
      {activeTab === 0 && <GarageManagement />}
      {activeTab === 1 && loadedTabs.has(1) && (
        <Suspense fallback={<Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>}>
          <ProductManagement />
        </Suspense>
      )}
      {activeTab === 2 && loadedTabs.has(2) && (
        <Suspense fallback={<Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>}>
          <AddonManagement />
        </Suspense>
      )}
      {activeTab === 3 && loadedTabs.has(3) && (
        <Suspense fallback={<Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>}>
          <BookingManagement />
        </Suspense>
      )}
    </Container>
  );
};

export default SystemManagement;